'use strict';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  Autocomplete,
  Popover,
  CircularProgress,
  Paper
} from '@mui/material';
import { fetchPolishCities, debounce, getPopularCities, getDistrictsForCity, fetchDistricts, getStreets, normalizePolishText } from '../services/streetApi';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

// Import logo
import logoImage from '../ux/catlogo4.png';
import { compressImage, compressProfilePhoto } from '../services/imageCompressor';
import PaymentModal from './PaymentModal';
import PremiumListingOption from './PremiumListingOption';
import PropertyDetailsModal from './PropertyDetailsModal';
import TenantDetailsModal from './TenantDetailsModal';
import { propertiesService, tenantsService } from '../services/supabaseService';
import { supabase } from '../services/supabaseService';

// Shared width style for text fields to keep consistent sizing
const fullWidthInputSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    width: '100%',
    minWidth: '200px'
  },
  '& .MuiInputBase-root': {
    width: '100%',
    minWidth: '200px'
  }
};

// Reusable date field for consistent sizing and behavior
export const AvailableFromField = ({ value, required, onChange }) => {
  const displayValue = value ? new Date(value).toLocaleDateString('pl-PL') : '';
  const inputRef = React.useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (input.showPicker) {
      try { input.showPicker(); } catch(e) { input.focus(); }
    } else {
      input.focus();
      input.click();
    }
  };

  return (
    <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={openPicker}>
      <TextField
        InputLabelProps={{ shrink: true }}
        fullWidth
        label="Dostępne od"
        value={displayValue}
        placeholder="Wybierz datę"
        required={required}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarIcon sx={{ color: '#666' }} />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' }
        }}
        sx={{
          cursor: 'pointer',
          pointerEvents: 'none',
          '& .MuiInputBase-root': {
            width: '100%',
            minWidth: '200px'
          }
        }}
      />
      <input
        ref={inputRef}
        type="date"
        value={value || ''}
        onChange={onChange}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 2,
          cursor: 'pointer',
          WebkitAppearance: 'none'
        }}
      />
    </Box>
  );
};

const AddListingFormNew = ({ open, onClose, onAdded, userId, userEmail, editMode = false, editData = null, onUpdated, tenantEditMode = false, tenantEditData = null, initialTab = 0, isFirstTime = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));



  // Sprawdź czy użytkownik jest zalogowany przy otwarciu modala
  useEffect(() => {
    if (open && !userId) {
      onClose();
    }
  }, [open, userId, onClose]);

  // Resetuj formularz po wylogowaniu użytkownika
  // Flaga: reset formularza tylko po udanym submicie, nie przy przypadkowym zamknięciu
  const resetOnCloseRef = React.useRef(false);

  const resetForms = React.useCallback(() => {
    setFormData({
      price: '', area: '', rooms: '', deposit: '', photos: [], city: '', district: '',
      street: '', buildingNumber: '', floor: '', totalFloors: '', rent: '', availableFrom: '',
      amenities: [], equipment: [], title: '', description: '', firstName: '',
      phone: '', email: ''
    });
    setTenantData({
      profilePhoto: null, firstName: '', age: '', profession: '', phone: '',
      email: '', monthlyIncome: '', priceRange: { min: '', max: '' }, preferredRooms: '',
      preferredArea: '', preferredAreaMin: '', preferredAreaMax: '', preferredCity: '',
      preferredDistrict: '', moveInDate: '', petOwner: false, smoker: false,
      preferredAmenities: [], description: '', references: ''
    });
    setValidationError('');
    setProgress(0);
    setIsSubmitting(false);
    setPrivacyPolicyAccepted(false);
    setIsPremiumSelected(false);
    setActiveTab(0);
    setButtonText('Opublikuj ogłoszenie');
    setCitySuggestions([]);
    setCityInputValue('');
    setDistrictSuggestions([]);
    setDistrictInputValue('');
    setStreetSuggestions([]);
    setStreetInputValue('');
    setTenantCitySuggestions([]);
    setTenantCityInputValue('');
    setTenantDistrictSuggestions([]);
    setTenantDistrictInputValue('');
  }, []);

  useEffect(() => {
    if (!userId) {
      // Reset property form
      setFormData({
        price: '', area: '', rooms: '', deposit: '', photos: [], city: '', district: '',
        street: '', buildingNumber: '', floor: '', totalFloors: '', rent: '', availableFrom: '',
        amenities: [], equipment: [], title: '', description: '', firstName: '',
        phone: '', email: ''
      });
      
      // Reset tenant form
      setTenantData({
        profilePhoto: null, firstName: '', age: '', profession: '', phone: '',
        email: '', monthlyIncome: '', priceRange: { min: '', max: '' }, preferredRooms: '',
        preferredArea: '', preferredAreaMin: '', preferredAreaMax: '', preferredCity: '',
        preferredDistrict: '', moveInDate: '', petOwner: false, smoker: false,
        preferredAmenities: [], description: '', references: ''
      });
    }
  }, [userId]);

  // Reset form state when modal closes

  useEffect(() => {
    if (!open) {
      if (resetOnCloseRef.current) {
        // Reset tylko po udanym submicie
        resetForms();
        resetOnCloseRef.current = false;
      } else {
        // Zamknięcie bez submitu — wyczyść tylko błędy walidacji i stan submitu
        setValidationError('');
        setIsSubmitting(false);
        setProgress(0);
      }
    }
  }, [open, resetForms]);

  // Initialize form data for edit mode
  useEffect(() => {

    
    if (editMode && editData && editData.id && open) {
      
      setFormData({
        price: editData.price || '',
        area: editData.area || '',
        rooms: editData.rooms != null ? editData.rooms : '',
        deposit: editData.deposit || '',
        photos: Array.isArray(editData.image_url) ? editData.image_url : 
               (editData.image_url ? [editData.image_url] : []),
        city: editData.city || '',
        district: editData.district || '',
        street: editData.street || '',
        buildingNumber: editData.building_number || '',
        floor: editData.floor || '',
        totalFloors: editData.total_floors || '',
        rent: editData.rent || '',
        availableFrom: editData.available_from || '',
        amenities: editData.amenities || [],
        equipment: editData.equipment || [],
        title: editData.title || '',
        description: editData.description || '',
        firstName: editData.first_name || '',
        phone: editData.phone || '',
        email: editData.email || ''
      });
      
      // Ustaw wartości wyświetlane w autocomplete inputach
      setCityInputValue(editData.city || '');
      setDistrictInputValue(editData.district || '');
      setStreetInputValue(editData.street || '');

      // Reset validation states for edit mode
      setValidationError('');
      setPrivacyPolicyAccepted(true); // Already accepted when creating
      setIsSubmitting(false); // Reset submission state
      setProgress(0); // Reset progress
      
    }
  }, [editMode, editData, open]);

  // Initialize form data for tenant edit mode
  useEffect(() => {
    if (tenantEditMode && tenantEditData && tenantEditData.id && open) {
      setActiveTab(1);
      setTenantData({
        profilePhoto: tenantEditData.avatar || null,
        firstName: tenantEditData.firstName || tenantEditData.name || '',
        age: tenantEditData.age ? String(tenantEditData.age) : '',
        profession: tenantEditData.profession || '',
        phone: tenantEditData.phone || '',
        email: tenantEditData.email || '',
        monthlyIncome: '',
        priceRange: {
          min: tenantEditData.priceMin ? String(tenantEditData.priceMin) : '',
          max: tenantEditData.priceMax ? String(tenantEditData.priceMax) : ''
        },
        preferredRooms: tenantEditData.roomsWanted != null && tenantEditData.roomsWanted !== '' ? String(tenantEditData.roomsWanted) : '',
        preferredArea: '',
        preferredAreaMin: tenantEditData.areaMin ? String(tenantEditData.areaMin) : '',
        preferredAreaMax: tenantEditData.areaMax ? String(tenantEditData.areaMax) : '',
        preferredCity: tenantEditData.city || '',
        preferredDistrict: tenantEditData.preferredDistrict || '',
        moveInDate: tenantEditData.moveInDate || '',
        petOwner: tenantEditData.petOwner || false,
        smoker: tenantEditData.smoker || false,
        preferredAmenities: Array.isArray(tenantEditData.equipment) ? tenantEditData.equipment : [],
        description: tenantEditData.description || '',
        references: tenantEditData.references || ''
      });
      setTenantCityInputValue(tenantEditData.city || '');
      setTenantDistrictInputValue(tenantEditData.preferredDistrict || '');
      setPrivacyPolicyAccepted(true);
      setValidationError('');
      setIsSubmitting(false);
      setProgress(0);
    }
  }, [tenantEditMode, tenantEditData, open]);

  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 = Mieszkanie, 1 = Najemca

  // Przy otwarciu nowego formularza ustaw zakładkę zgodną z aktualnym widokiem portalu
  useEffect(() => {
    if (open && !editMode && !tenantEditMode) {
      setActiveTab(initialTab);
    }
  }, [open, editMode, tenantEditMode, initialTab]);

  // Update button text when edit mode changes
  useEffect(() => {
    setButtonText(
      (editMode || tenantEditMode)
        ? (activeTab === 0 ? 'Zapisz zmiany' : 'Zaktualizuj profil najemcy')
        : (activeTab === 0 ? 'Opublikuj ogłoszenie' : 'Opublikuj profil najemcy')
    );
  }, [editMode, tenantEditMode, activeTab]);

  // Form state
  const [formData, setFormData] = useState({
    // Podstawowe dane
    price: '',
    area: '',
    rooms: '',
    deposit: '',
    
    // Zdjęcia
    photos: [],
    
    // Adres
    city: '',
    district: '',
    street: '',
    buildingNumber: '',
    
    // Szczegóły budynku
    floor: '',
    totalFloors: '',
    rent: '',
    availableFrom: '',
    
    // Udogodnienia
    amenities: [],
    
    // Wyposażenie
    equipment: [],
    
    // Opis
    title: '',
    description: '',
    
    // Kontakt
    firstName: '',
    phone: '',
    email: ''
  });

  // Tenant form state
  const [tenantData, setTenantData] = useState({
    profilePhoto: null,
    firstName: '',
    age: '',
    profession: '',
    phone: '',
    email: '',
    monthlyIncome: '',
    priceRange: { min: '', max: '' },
    preferredRooms: '',
    preferredArea: '',
    preferredAreaMin: '',
    preferredAreaMax: '',
    preferredCity: '',
    preferredDistrict: '',
    moveInDate: '',
    petOwner: false,
    smoker: false, // false = niepalący, true = palący
    preferredAmenities: [],
    description: '',
    references: ''
  });

  // Payment modal state
  // Payment modal logic zakomentowany
  // Progress bar state
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [tenantDescTouched, setTenantDescTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [tenantEmailTouched, setTenantEmailTouched] = useState(false);
  const [streetTouched, setStreetTouched] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [buttonText, setButtonText] = useState(
    (editMode || tenantEditMode)
      ? (activeTab === 0 ? 'Zapisz zmiany' : 'Zaktualizuj profil najemcy')
      : (activeTab === 0 ? 'Opublikuj ogłoszenie' : 'Opublikuj profil najemcy')
  );

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIsEditMode, setPreviewIsEditMode] = useState(false);
  const [previewProperty, setPreviewProperty] = useState(null);
  const [tenantPreviewOpen, setTenantPreviewOpen] = useState(false);
  const [previewTenant, setPreviewTenant] = useState(null);

  // Validation error state
  const [validationError, setValidationError] = useState('');

  // City popover state
  const [cityAnchorEl, setCityAnchorEl] = useState(null);
  const [tenantCityAnchorEl, setTenantCityAnchorEl] = useState(null);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [tenantPrivacyAccepted, setTenantPrivacyAccepted] = useState(false);
  const [isPremiumSelected, setIsPremiumSelected] = useState(false);
  
  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [cityInputValue, setCityInputValue] = useState('');
  
  // District autocomplete state
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [districtInputValue, setDistrictInputValue] = useState('');
  
  // Tenant city/district autocomplete state
  const [tenantCitySuggestions, setTenantCitySuggestions] = useState([]);
  const [isLoadingTenantCities, setIsLoadingTenantCities] = useState(false);
  const [tenantCityInputValue, setTenantCityInputValue] = useState('');
  const [tenantDistrictSuggestions, setTenantDistrictSuggestions] = useState([]);
  const [tenantDistrictInputValue, setTenantDistrictInputValue] = useState('');
  
  // Street autocomplete state
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [isLoadingStreets, setIsLoadingStreets] = useState(false);
  const [streetInputValue, setStreetInputValue] = useState('');
  const [streetDropdownOpen, setStreetDropdownOpen] = useState(false);
  const streetInputRef = useRef(null);

  // TEMPORARY: Disable field validation for development
  const DEVELOPMENT_MODE = true;
  const isFieldRequired = !DEVELOPMENT_MODE;

  // Dostępne udogodnienia (z głównej aplikacji)
  const availableAmenities = [
    'Balkon',
    'Garaż',
    'Winda',
    'Piwnica',
    'Internet',
    'Ogródek',
    'Ostatnie piętro',
    'Parter'
  ];

  // Dostępne wyposażenie
  const availableEquipment = [
    'Pralka',
    'Zmywarka',
    'Piekarnik',
    'Telewizor',
  'WiFi',
  'Klimatyzacja',
  'Kuchenka mikrofalowa'
  ];

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Resetuj tekst przycisku przy zmianie taba
    setButtonText(
      editMode 
        ? (newValue === 0 ? 'Zapisz zmiany' : 'Zaktualizuj profil najemcy')
        : (newValue === 0 ? 'Opublikuj ogłoszenie' : 'Opublikuj profil najemcy')
    );
  };

  // Property form handlers
  // Sanitizacja tekstu — usuwa tagi HTML, ogranicza długość, trimuje
  const sanitizeText = (value, maxLength = 2000) => {
    if (typeof value !== 'string') return value;
    return value
      .replace(/<[^>]*>/g, '')          // usuwa tagi HTML (<script>, <img>, itp.)
      .replace(/javascript:/gi, '')      // usuwa javascript: URI
      .replace(/on\w+\s*=/gi, '')        // usuwa event handlery (onclick=, onerror=)
      .slice(0, maxLength);
  };

  // Normalizacja nazw ulic: "MALEJ LAKI" → "Małej Łąki", "al." / "aleja" → z małej
  const normalizeStreetName = (value) => {
    if (!value) return value;
    const lowerPrefixes = ['al.', 'al', 'aleja', 'ul.', 'ul', 'os.', 'os', 'pl.', 'pl', 'plac'];
    return value
      .split(/(\s+)/) // zachowaj spacje
      .map((part, idx, arr) => {
        if (/^\s+$/.test(part)) return part; // spacja — zostaw
        const lower = part.toLowerCase();
        // Pierwszy wyraz lub wyraz po spacji — sprawdź prefiksy
        const wordIndex = arr.slice(0, idx).filter(p => !/^\s+$/.test(p)).length;
        if (wordIndex === 0 && lowerPrefixes.includes(lower)) {
          return lower; // "AL" / "ALEJA" → "al" / "aleja"
        }
        // Reszta: pierwsza duża, reszta mała
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join('');
  };

  const handleInputChange = (field) => (event) => {
    const limits = { title: 200, description: 3000, buildingNumber: 20, floor: 5, totalFloors: 5 };
    let sanitized = sanitizeText(event.target.value, limits[field] || 500);
    if (field === 'street') {
      sanitized = normalizeStreetName(sanitized);
    }
    setFormData(prev => ({
      ...prev,
      [field]: sanitized
    }));
    // Wyczyść błąd walidacji gdy użytkownik edytuje formularz
    if (validationError) {
      setValidationError('');
    }
  };

  // Tenant form handlers
  const handleTenantInputChange = (field) => (event) => {
    const limits = { firstName: 100, phone: 20, email: 200, bio: 1000, description: 650 };
    const sanitized = sanitizeText(event.target.value, limits[field] || 500);
    setTenantData(prev => ({
      ...prev,
      [field]: sanitized
    }));
  };

  const handleTenantPriceRangeChange = (field) => (event) => {
    setTenantData(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [field]: event.target.value
      }
    }));
  };

  const handleTenantCheckboxChange = (field) => (event) => {
    setTenantData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleTenantAmenityChange = (amenity) => (event) => {
    setTenantData(prev => {
      const amenities = prev.preferredAmenities || [];
      if (event.target.checked) {
        return { ...prev, preferredAmenities: [...amenities, amenity] };
      } else {
        return { ...prev, preferredAmenities: amenities.filter(a => a !== amenity) };
      }
    });
  };

  // City popover handlers
  const handleCityClick = (event) => {
    setCityAnchorEl(event.currentTarget);
    
    // Gdy otwiera się popover dla miasta, pokaż popularne miasta jak Gratka.pl
    if (cityInputValue.length === 0) {
      const popularCities = getPopularCities();
      setCitySuggestions(popularCities);
    }
  };

  const handleCityClose = () => {
    setCityAnchorEl(null);
  };

  // Tenant city popover handlers
  const handleTenantCityClick = (event) => {
    setTenantCityAnchorEl(event.currentTarget);
    
    if (tenantCityInputValue.length === 0) {
      const popularCities = getPopularCities();
      setTenantCitySuggestions(popularCities);
    }
  };

  const handleTenantCityClose = () => {
    setTenantCityAnchorEl(null);
  };

  const handleCityInputChange = (event, newInputValue) => {
    setCityInputValue(newInputValue);
    fetchCities(newInputValue);
  };

  const handleCityChange = async (event, newValue) => {
    const cityName = newValue?.name || newValue || '';
    const cityActuallyChanged = cityName !== formData.city;
    setFormData(prev => ({
      ...prev,
      city: cityName,
      district: cityActuallyChanged ? '' : prev.district, // Reset district only when city changes
      street: cityActuallyChanged ? '' : prev.street // Reset street only when city changes
    }));
    setCityInputValue(cityName);
    
    // Load districts for the selected city
    if (cityName) {
      if (cityActuallyChanged) {
        setDistrictSuggestions(getDistrictsForCity(cityName)); // natychmiastowy fallback
        setDistrictInputValue('');
      }
      setCityAnchorEl(null);
      try {
        const districts = await fetchDistricts(cityName, '');
        if (districts.length > 0) setDistrictSuggestions(districts);
      } catch {}
    } else {
      setDistrictSuggestions([]);
      setDistrictInputValue('');
    }
    
    if (cityActuallyChanged) {
      setStreetSuggestions([]);
      setStreetInputValue('');
      setStreetDropdownOpen(false);
    }
  };



  // District autocomplete functions
  const handleDistrictInputChange = (event, newInputValue) => {
    setDistrictInputValue(newInputValue);
  };

  const handleDistrictChange = (event, newValue) => {
    const districtName = newValue?.name || newValue || '';
    setFormData(prev => ({
      ...prev,
      district: districtName
    }));
    setDistrictInputValue(districtName);
  };

  // City autocomplete functions
  const fetchCities = useCallback(
    debounce(async (query) => {
      // Jeśli pole jest puste, pokaż popularne miasta (jak Gratka.pl)
      if (query.length === 0) {
        const popularCities = getPopularCities();
        setCitySuggestions(popularCities);
        return;
      }
      
      if (query.length < 2) {
        setCitySuggestions([]);
        return;
      }

      setIsLoadingCities(true);
      try {
        const cities = await fetchPolishCities(query);
        setCitySuggestions(cities);
      } catch (error) {
        setCitySuggestions([]);
      } finally {
        setIsLoadingCities(false);
      }
    }, 300),
    []
  );

  // Street autocomplete functions
  const fetchStreets = useCallback(
    debounce(async (cityName, query) => {
      if (!cityName || query.length < 2) {
        setStreetSuggestions([]);
        return;
      }
      setIsLoadingStreets(true);
      try {
        const streets = await getStreets(cityName, query);
        setStreetSuggestions(streets);
      } catch (error) {
        setStreetSuggestions([]);
      } finally {
        setIsLoadingStreets(false);
      }
    }, 300),
    []
  );

  const handleStreetInputChange = (newValue) => {
    const normalized = normalizeStreetName(newValue);
    setStreetInputValue(normalized);
    setFormData(prev => ({ ...prev, street: normalized }));
    if (!formData.city || newValue.length < 2) {
      setStreetSuggestions([]);
      setIsLoadingStreets(false);
      setStreetDropdownOpen(false);
    } else {
      setIsLoadingStreets(true);
      setStreetDropdownOpen(true);
      fetchStreets(formData.city, newValue);
    }
  };

  const handleSelectStreet = (street) => {
    const name = normalizeStreetName(street?.name || street || '');
    setStreetInputValue(name);
    setFormData(prev => ({ ...prev, street: name }));
    setStreetDropdownOpen(false);
    setStreetSuggestions([]);
    if (validationError) setValidationError('');
  };

  // Keep handleStreetChange as alias for backward compatibility
  const handleStreetChange = handleSelectStreet;

  // Walidacja bezpieczeństwa zdjęcia: magic bytes + rozmiar + test renderowania
  const validateImageFile = (file) => new Promise((resolve) => {
    const MAX_SIZE_MB = 8;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      resolve(`Plik jest za duży — maksymalny rozmiar to ${MAX_SIZE_MB} MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result).subarray(0, 12);
      // JPEG magic bytes: FF D8 FF
      const isJpeg = arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF;
      // PNG magic bytes: 89 50 4E 47
      const isPng = arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47;
      // WebP magic bytes: RIFF????WEBP (52 49 46 46 xx xx xx xx 57 45 42 50)
      const isWebp = arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46
        && arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50;
      if (!isJpeg && !isPng && !isWebp) {
        resolve(`problem_format`);
        return;
      }
      // Test renderowania — przeglądarka próbuje załadować plik jako obraz
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(null); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(`problem_render`); };
      img.src = url;
    };
    reader.readAsArrayBuffer(file.slice(0, 12));
  });

  const handleProfilePhotoUpload = async (event) => {
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const input = event.target;
    const file = input.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setValidationError('Zdjęcie profilowe musi być w formacie JPG, PNG lub WebP');
      input.value = '';
      return;
    }
    const error = await validateImageFile(file);
    if (error === 'problem_format') {
      setValidationError('Zdjęcie profilowe musi być w formacie JPG, PNG lub WebP');
      input.value = '';
      return;
    }
    if (error) {
      setValidationError('Twój plik wygląda na uszkodzony — spróbuj innego pliku.');
      input.value = '';
      return;
    }
    // Kompresuj zdjęcie profilowe (max 800px)
    const compressed = await compressProfilePhoto(file);
    setTenantData(prev => ({
      ...prev,
      profilePhoto: compressed
    }));
    if (validationError) {
      setValidationError('');
    }
    input.value = '';
  };

  // Tenant city/district handlers
  const fetchTenantCities = useCallback(
    debounce(async (query) => {
      // Jeśli pole jest puste, pokaż popularne miasta (jak w zakładce mieszkania)
      if (query.length === 0) {
        const popularCities = getPopularCities();
        setTenantCitySuggestions(popularCities);
        return;
      }
      
      if (query.length < 2) {
        setTenantCitySuggestions([]);
        return;
      }

      setIsLoadingTenantCities(true);
      try {
        const cities = await fetchPolishCities(query);
        setTenantCitySuggestions(cities);
      } catch (error) {
        setTenantCitySuggestions([]);
      } finally {
        setIsLoadingTenantCities(false);
      }
    }, 300),
    []
  );

  const handleTenantCityInputChange = (event, newInputValue) => {
    setTenantCityInputValue(newInputValue);
    fetchTenantCities(newInputValue);
  };

  const handleTenantCityChange = async (event, newValue) => {
    const cityName = newValue?.name || newValue || '';
    setTenantData(prev => ({
      ...prev,
      preferredCity: cityName,
      preferredDistrict: ''
    }));
    setTenantCityInputValue(cityName);
    
    if (cityName) {
      setTenantDistrictSuggestions(getDistrictsForCity(cityName)); // natychmiastowy fallback
      setTenantDistrictInputValue('');
      try {
        const districts = await fetchDistricts(cityName, '');
        if (districts.length > 0) setTenantDistrictSuggestions(districts);
      } catch {}
    } else {
      setTenantDistrictSuggestions([]);
      setTenantDistrictInputValue('');
    }
  };

  const handleTenantDistrictInputChange = (event, newInputValue) => {
    setTenantDistrictInputValue(newInputValue);
  };

  const handleTenantDistrictChange = (event, newValue) => {
    const districtName = newValue?.name || newValue || '';
    setTenantData(prev => ({
      ...prev,
      preferredDistrict: districtName
    }));
    setTenantDistrictInputValue(districtName);
  };

  const handleCheckboxChange = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
    // Wyczyść błąd walidacji gdy użytkownik edytuje formularz
    if (validationError) {
      setValidationError('');
    }
  };

  const handlePhotoUpload = async (event) => {
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const input = event.target;
    const allFiles = Array.from(input.files || []);
    if (allFiles.length === 0) return;
    const invalidTypes = allFiles.filter(f => !ALLOWED_TYPES.includes(f.type));
    if (invalidTypes.length > 0) {
      setValidationError('Zdjęcia muszą być w formacie JPG, PNG lub WebP');
      input.value = '';
      return;
    }
    // Sprawdź magic bytes + rozmiar + renderowanie dla każdego pliku
    for (const file of allFiles) {
      const err = await validateImageFile(file);
      if (err === 'problem_format') {
        setValidationError('Zdjęcia muszą być w formacie JPG, PNG lub WebP');
        input.value = '';
        return;
      }
      if (err) {
        setValidationError('Twój plik wygląda na uszkodzony — spróbuj innego pliku.');
        input.value = '';
        return;
      }
    }
    const files = allFiles;
    if (formData.photos.length + files.length <= 10) {
      // Kompresuj zdjęcia przed dodaniem (resize + WebP)
      const compressedFiles = await Promise.all(
        files.map(file => compressImage(file))
      );
      setFormData(prev => {
        const newPhotos = [...prev.photos, ...compressedFiles];
        return {
          ...prev,
          photos: newPhotos
        };
      });
      // Wyczyść błąd walidacji gdy użytkownik dodaje zdjęcia
      if (validationError) {
        setValidationError('');
      }
    } else {
      setValidationError(`Możesz dodać maksymalnie 10 zdjęć (obecnie: ${formData.photos.length})`);
    }
    input.value = '';
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) return;
    setFormData(prev => {
      const newPhotos = [...prev.photos];
      const [moved] = newPhotos.splice(dragIndex, 1);
      newPhotos.splice(dropIndex, 0, moved);
      return { ...prev, photos: newPhotos };
    });
    setDragIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    // W trybie edycji wyczyść błędy na początku
    if (editMode) {
      setValidationError('');
    } else {
    }
    
    // Walidacja wymaganych pól przed publikacją
    const missingFields = validateRequiredFields();
    
    if (missingFields.length > 0) {
      setValidationError(`Popraw następujące błędy:\n• ${missingFields.join('\n• ')}`);
      return;
    }
    
    // Wyczyść błędy jeśli wszystko OK
    setValidationError('');

    // 1. Upload photos to Supabase Storage
    const uploadPhotos = async () => {
      if (!formData.photos || formData.photos.length === 0) return [];
      const urls = [];
      for (let file of formData.photos) {
        if (!file) {
          continue;
        }
        
        // W trybie edycji, zdjęcia mogą być już URL-ami (stringi) zamiast obiektów File
        if (typeof file === 'string') {
          urls.push(file);
          continue;
        }
        
        // Tylko nowe pliki (File obiekty) muszą być uploadowane
        if (!(file instanceof File)) {
          continue;
        }
        
        const fileExt = file.name.split('.').pop().toLowerCase();
        const fileName = `${userId}/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        // Zawsze uploaduj jako JPEG — nawet jeśli plik ma type image/webp (stare sesje)
        const uploadFile = file.type === 'image/webp'
          ? new File([file], fileName, { type: 'image/jpeg' })
          : file;
        const { data, error } = await supabase.storage.from('property-photos').upload(fileName, uploadFile, {
          contentType: 'image/jpeg',
          upsert: false
        });
        if (error || !data) {
          setValidationError('Błąd uploadu zdjęcia: ' + (error?.message || 'Brak danych'));
          continue;
        }
        // Generuj publiczny URL ręcznie
        const publicUrl = `https://jjbyrtlbadzrzilkwceg.supabase.co/storage/v1/object/public/property-photos/${fileName}`;
        urls.push(publicUrl);
      }
      return urls;
    };

    setProgress(0);
    setIsSubmitting(true);
    const imageUrls = await uploadPhotos();
    // 2. Mapowanie pól do bazy
    const propertyData = {
      user_id: userId,
      price: formData.price,
      area: formData.area,
      rooms: formData.rooms,
      deposit: formData.deposit,
      image_url: imageUrls,
      city: formData.city,
      street: formData.street,
      building_number: formData.buildingNumber,
      district: formData.district,
      floor: formData.floor,
      total_floors: formData.totalFloors,
      rent: formData.rent,
      available_from: formData.availableFrom,
      amenities: formData.amenities,
      equipment: formData.equipment,
      title: formData.title,
      description: formData.description,
      first_name: formData.firstName,

      phone: formData.phone,
      email: formData.email,
      ...(!editMode && { privacy_accepted_at: new Date().toISOString() }),
      ...(isPremiumSelected && !editMode && {
        is_premium: true,
        premium_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      })
    };

    // Geocoding — zapisz lat/lng przy nowym ogłoszeniu (raz, na starcie)
    if (!editMode && (formData.city || formData.street)) {
      try {
        const addressQ = [formData.street, formData.buildingNumber, formData.city]
          .filter(Boolean).join(', ');
        const geoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?` + new URLSearchParams({
            q: addressQ,
            format: 'json',
            limit: 5,
            countrycodes: 'pl',
            'accept-language': 'pl',
          })
        );
        const geoData = await geoRes.json();
        if (geoData?.length > 0) {
          // Preferuj wyniki typu highway (ulica), żeby uniknąć trafienia
          // w budynki/obiekty o podobnej nazwie (np. "Muzeum Niepodległości")
          const streetResult = geoData.find(r => r.class === 'highway') || geoData[0];
          propertyData.lat = parseFloat(streetResult.lat);
          propertyData.lng = parseFloat(streetResult.lon);
        }
      } catch {
        // geocoding nieobowiązkowy — nie blokuj zapisu
      }
    }
    const wasEditing = editMode; // capture before any async callbacks may change it
    try {
      if (editMode && editData) {
        // Tryb edycji - aktualizuj istniejące ogłoszenie
        
        const { data, error } = await supabase
          .from('properties')
          .update(propertyData)
          .eq('id', editData.id)
          .eq('user_id', userId); // Dodatkowe zabezpieczenie
        
        if (error) throw error;
        
        
        // Wywołaj callback dla aktualizacji
        if (onUpdated) {
          await onUpdated(editData.id, propertyData);
        }
      } else {
        // Tryb dodawania - dodaj nowe ogłoszenie
        const addedProperty = await propertiesService.add(propertyData);
        
        // Email potwierdzający (fire & forget — nie blokuje UI)
        if (userEmail) {
          fetch(`https://jjbyrtlbadzrzilkwceg.supabase.co/functions/v1/send-listing-confirmation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: userEmail,
              firstName: formData.firstName || '',
              listingTitle: formData.title || `Mieszkanie, ${formData.city}`,
              listingId: addedProperty?.id || '',
              city: formData.city || '',
              price: formData.price || 0,
            }),
          }).catch(() => {}); // cicho ignoruj błędy wysyłki
        }

        // Odśwież listę nieruchomości w App.js
        if (onAdded) {
          await onAdded('property');
        } else {
        }
      }
      
      // Animacja paska progresu
      let value = 0;
      const interval = setInterval(() => {
        value += 10;
        setProgress(value);
        if (value >= 100) {
          clearInterval(interval);
          setIsSubmitting(false);
          setPublishSuccess(true);
          resetOnCloseRef.current = true;
          setButtonText(wasEditing ? 'Zmiany zapisane' : 'Ogłoszenie dodane');
          if (!wasEditing && window.gtag) window.gtag('event', 'publish_ogloszenie_success', { event_category: 'engagement' });
          setTimeout(() => {
            setPublishSuccess(false);
            onClose();
          }, 3000);
        }
      }, 80);
    } catch (err) {
      if (err.message.includes('email jest już używany')) {
        setValidationError(err.message);
      } else {
        setValidationError('Błąd podczas zapisu do bazy: ' + err.message);
      }
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
  // alert('Płatność zakończona pomyślnie! Ogłoszenie zostało opublikowane.');
  // onClose();
  };

  const handlePaymentClose = () => {
  // setPaymentModalOpen(false);
  };

  // Wrapper funkcji dla PropertyDetailsModal
  const handlePropertyPublishFromModal = async () => {
    try {
      const mockEvent = {
        preventDefault: () => {}
      };
      await handleSubmit(mockEvent);
      // handleSubmit sam zamknie modal po 3s (setPublishSuccess + setTimeout onClose)
      // Zamykamy tylko preview — główny modal zamknie się przez onClose w handleSubmit
      setTimeout(() => setPreviewOpen(false), 3100);
    } catch (error) {
      // Nie zamykamy modalu w przypadku błędu
    }
  };

  // Wrapper funkcji dla TenantDetailsModal
  const handleTenantPublishFromModal = async () => {
    // Symuluj event preventDefault
    const mockEvent = {
      preventDefault: () => {}
    };
    // Jeśli użytkownik dotarł do podglądu, traktujemy to jako akceptację regulaminu
    await handleTenantSubmit(mockEvent, true);
    // setTenantPreviewOpen(false) zostanie wywołane przez onClose po sukcesie
  };

  const handleTenantSubmit = async (e, bypassPrivacy = false) => {
    e.preventDefault();
    
    // Szczegółowa walidacja pól najemcy
    const missingFields = [];
    
    if (!tenantData.profilePhoto) {
      missingFields.push('Zdjęcie profilowe');
    }
    if (!tenantData.firstName || tenantData.firstName.trim() === '') {
      missingFields.push('Imię');
    } else if (tenantData.firstName.trim().length < 3) {
      missingFields.push('Imię (min. 3 znaki)');
    }
    if (!tenantData.age || tenantData.age.toString().trim() === '') {
      missingFields.push('Wiek');
    }
    if (!tenantData.email || tenantData.email.trim() === '') {
      missingFields.push('Email');
    } else if (!tenantData.email.includes('@') || tenantData.email.indexOf('@') < 1 || tenantData.email.length - tenantData.email.indexOf('@') - 1 < 4) {
      missingFields.push('Email (niepoprawny format)');
    }
    if (!tenantData.preferredCity || tenantData.preferredCity.trim() === '') {
      missingFields.push('Miasto');
    }
    if (!tenantData.preferredRooms && tenantData.preferredRooms !== 0) {
      missingFields.push('Liczba pokoi');
    }
    if (!tenantData.description || tenantData.description.trim() === '') {
      missingFields.push('Opis');
    } else if (tenantData.description.trim().length < 30) {
      missingFields.push(`Opis (min. 30 znaków, wpisano: ${tenantData.description.trim().length})`);
    }
    if (!tenantEditMode && !tenantPrivacyAccepted && !bypassPrivacy) {
      missingFields.push('Zgoda na politykę prywatności i regulamin');
    }

    if (missingFields.length > 0) {
      setValidationError(`Uzupełnij: ${missingFields.join(', ')}`);
      return;
    }
    
    // Wyczyść błędy jeśli wszystko OK
    setValidationError('');

    try {
      setProgress(0);
      setIsSubmitting(true);
      
      // Upload profile photo if exists
      let profilePhotoUrl = '';
      if (tenantData.profilePhoto) {
        // W trybie edycji, zdjęcie może być już URL-em (string) zamiast obiektu File
        if (typeof tenantData.profilePhoto === 'string') {
          profilePhotoUrl = tenantData.profilePhoto;
        } else if (tenantData.profilePhoto instanceof File) {
          // Tylko nowe pliki (File obiekty) muszą być uploadowane
          const fileExt = tenantData.profilePhoto.name.split('.').pop().toLowerCase();
        const fileName = `${userId}/tenant_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.jpg`;
        const uploadFile = tenantData.profilePhoto.type === 'image/webp'
          ? new File([tenantData.profilePhoto], fileName, { type: 'image/jpeg' })
          : tenantData.profilePhoto;
        const { data, error } = await supabase.storage
          .from('property-photos')
          .upload(fileName, uploadFile, { contentType: 'image/jpeg', upsert: false });
        
        if (error) {
          setValidationError('Błąd uploadu zdjęcia: ' + error.message);
          setIsSubmitting(false);
          return;
        }
        
        profilePhotoUrl = `https://jjbyrtlbadzrzilkwceg.supabase.co/storage/v1/object/public/property-photos/${fileName}`;
        } else {
        }
      }

      // Map tenant data to database format
      const tenantProfileData = {
        first_name: tenantData.firstName,
        phone: tenantData.phone || null,
        age: tenantData.age ? parseInt(tenantData.age) : null,
        profession: tenantData.profession,
        email: tenantData.email,
        city: tenantData.preferredCity,
        preferred_district: tenantData.preferredDistrict,
        preferred_rooms: tenantData.preferredRooms != null && tenantData.preferredRooms !== '' ? parseInt(tenantData.preferredRooms) : null,
        price_min: tenantData.priceRange.min ? parseInt(tenantData.priceRange.min) : null,
        price_max: tenantData.priceRange.max ? parseInt(tenantData.priceRange.max) : null,
        area_min: tenantData.preferredAreaMin ? parseInt(tenantData.preferredAreaMin) : null,
        area_max: tenantData.preferredAreaMax ? parseInt(tenantData.preferredAreaMax) : null,
        move_in_date: tenantData.moveInDate,
        pet_owner: tenantData.petOwner,
        smoker: tenantData.smoker,
        description: tenantData.description,
        profile_photo_url: profilePhotoUrl,
        references: tenantData.references || null,
        equipment: tenantData.preferredAmenities && tenantData.preferredAmenities.length > 0 
          ? tenantData.preferredAmenities 
          : null,
        user_id: userId, // Przypisz ID zalogowanego użytkownika
        ...(!tenantEditMode && { privacy_accepted_at: new Date().toISOString() })
      };


      // Submit to Supabase
      let result;
      if (tenantEditMode && tenantEditData?.id) {
        // Update existing profile (exclude user_id from update payload)
        const { user_id, ...updateData } = tenantProfileData;
        result = await tenantsService.update(tenantEditData.id, userId, updateData);
      } else {
        result = await tenantsService.add(tenantProfileData);
      }
      
      // Animacja paska progresu
      let value = 0;
      const interval = setInterval(() => {
        value += 10;
        setProgress(value);
        if (value >= 100) {
          clearInterval(interval);
          setIsSubmitting(false);
          setPublishSuccess(true);
          resetOnCloseRef.current = true;
          setButtonText(tenantEditMode ? 'Profil zaktualizowany' : 'Profil opublikowany');
          if (!tenantEditMode && window.gtag) window.gtag('event', 'publish_profil_najemcy_success', { event_category: 'engagement' });
          setTimeout(async () => {
            if (onAdded) {
              await onAdded('tenant');
            }
            setPublishSuccess(false);
            onClose();
          }, 3000);
        }
      }, 80);

    } catch (error) {
      if (error.message.includes('email jest już używany')) {
        setValidationError(error.message);
      } else {
        setValidationError('Błąd podczas publikacji profilu: ' + error.message);
      }
      setIsSubmitting(false);
    }
  };

  const validateRequiredFields = () => {
    
    // Lista wymaganych pól (wszystkie oprócz buildingNumber, amenities i equipment)
    const requiredFields = [
      { field: 'price', label: 'Cena' },
      { field: 'area', label: 'Powierzchnia' },
      { field: 'rooms', label: 'Liczba pokoi' },
      { field: 'deposit', label: 'Wysokość kaucji' },
      { field: 'city', label: 'Miasto' },
      { field: 'district', label: 'Dzielnica' },
      { field: 'floor', label: 'Piętro' },
      { field: 'totalFloors', label: 'Liczba pięter' },
      { field: 'rent', label: 'Czynsz' },
      { field: 'availableFrom', label: 'Dostępne od' },
      { field: 'title', label: 'Tytuł ogłoszenia' },
      { field: 'description', label: 'Opis' },
      { field: 'firstName', label: 'Imię' },

      { field: 'phone', label: 'Numer telefonu' },
      { field: 'email', label: 'Email' }
    ];

    const missingFields = [];
    
    requiredFields.forEach(({ field, label }) => {
      const value = formData[field];
      if (!value || value.toString().trim() === '') {
        missingFields.push(label);
      }
    });

    // Walidacja zdjęć - minimum 3
    if (!formData.photos || formData.photos.length < 3) {
      missingFields.push(`Zdjęcia — dodaj minimum 3 zdjęcia (dodano: ${formData.photos?.length || 0} z 3 wymaganych)`);
    }

    // Walidacja tytułu - minimum 28 znaków
    if (formData.title && formData.title.trim().length < 28) {
      missingFields.push(`Tytuł ogłoszenia (min. 28 znaków, wpisano: ${formData.title.trim().length})`);
    }

    // Walidacja opisu - minimum 100 znaków
    if (formData.description && formData.description.trim().length < 100) {
      missingFields.push(`Opis (min. 100 znaków, wpisano: ${formData.description.trim().length})`);
    }

    // Walidacja imienia - min. 3 znaki
    if (formData.firstName && formData.firstName.trim().length < 3) {
      missingFields.push('Imię (min. 3 znaki)');
    }

    // Walidacja telefonu - dokładnie 9 cyfr
    if (formData.phone && formData.phone.replace(/\D/g, '').length !== 9) {
      missingFields.push('Numer telefonu (dokładnie 9 cyfr)');
    }

    // Dodatkowa walidacja email
    if (formData.email && (!formData.email.includes('@') || formData.email.indexOf('@') < 1 || formData.email.length - formData.email.indexOf('@') - 1 < 4)) {
      missingFields.push('Email (niepoprawny format)');
    }

    // Walidacja polityki prywatności
    if (!editMode && !privacyPolicyAccepted) {
      missingFields.push('Zgoda na politykę prywatności i regulamin');
    }

    return missingFields;
  };

  const handlePreview = () => {
    
    // W trybie edycji wyczyść błędy na początku
    if (editMode) {
      setValidationError('');
    }
    
    // Walidacja wymaganych pól
    const missingFields = validateRequiredFields();
    
    if (missingFields.length > 0) {
      setValidationError(`Popraw następujące błędy:\n• ${missingFields.join('\n• ')}`);
      return;
    }
    
    // Wyczyść błędy jeśli wszystko OK
    setValidationError('');

    // Przekształć dane z formularza do formatu wymaganego przez PropertyDetailsModal
    // Konwertuj zdjęcia z obiektów File na URL-e do podglądu
    const photoUrls = formData.photos.map(photo => {
      if (photo instanceof File) {
        return URL.createObjectURL(photo);
      }
      return photo;
    });

    const previewData = {
      id: 'preview',
      price: formData.price || '0',
      area: formData.area || '0',
      rooms: formData.rooms || '0',
      deposit: formData.deposit || '0',
      image_url: photoUrls,
      city: formData.city || '',
      street: formData.street || '',
      building_number: formData.buildingNumber || '',
      district: formData.district || '',
      floor: formData.floor || '',
      total_floors: formData.totalFloors || '',
      rent: formData.rent || '0',
      available_from: formData.availableFrom || '',
      amenities: formData.amenities || [],
      equipment: formData.equipment || [],
      title: formData.title || 'Tytuł ogłoszenia',
      description: formData.description || '',
      first_name: formData.firstName || '',

      phone: formData.phone || '',
      email: formData.email || ''
    };

    setPreviewProperty(previewData);
    setPreviewIsEditMode(editMode);
    setPreviewOpen(true);
  };

  const handleTenantPreview = () => {
    // Walidacja - wymagane zdjęcie profilowe
    if (!tenantData.profilePhoto) {
      setValidationError('Dodaj zdjęcie profilowe, aby zobaczyć podgląd profilu');
      return;
    }

    // Wyczyść błędy jeśli wszystko OK
    setValidationError('');

    // Przekształć dane najemcy do formatu wymaganego przez TenantDetailsModal
    const photoUrl = tenantData.profilePhoto instanceof File
      ? URL.createObjectURL(tenantData.profilePhoto)
      : tenantData.profilePhoto;

    // Stwórz string z budżetem
    const priceRangeString = tenantData.priceRange?.min && tenantData.priceRange?.max 
      ? `${tenantData.priceRange.min} - ${tenantData.priceRange.max} zł`
      : '';

    const previewData = {
      id: 'preview',
      avatar: photoUrl, // Zmienione z profile_photo_url na avatar
      name: tenantData.firstName || '', // Tylko imię
      age: tenantData.age || '',
      profession: tenantData.profession || '',
      phone: tenantData.phone || '',
      email: tenantData.email || '',
      city: tenantData.preferredCity || '',
      preferredDistrict: tenantData.preferredDistrict || '',
      roomsWanted: tenantData.preferredRooms != null && tenantData.preferredRooms !== '' ? tenantData.preferredRooms : '',
      priceRange: priceRangeString, // Sformatowany string z budżetem
      moveInDate: tenantData.moveInDate || '',
      petOwner: tenantData.petOwner || false,
      smoker: tenantData.smoker || false,
      description: tenantData.description || '',
      equipment: tenantData.preferredAmenities || []
    };

    setPreviewTenant(previewData);
    setTenantPreviewOpen(true);
  };

  // DEBUG: List all files in property-photos bucket and show public URLs
  const handleDebugListPhotos = async () => {
    try {
  const { data, error } = await supabase.storage.from('property-photos').list('');
      if (error) {
        alert('Błąd pobierania listy plików: ' + error.message);
        return;
      }
      if (!data || data.length === 0) {
        alert('Brak plików w property-photos');
        return;
      }
      const urls = data.map(file => {
  // Supabase expects path relative to bucket root, so use file.name only
  const { publicUrl } = supabase.storage.from('property-photos').getPublicUrl(file.name);
        return { name: file.name, url: publicUrl };
      });
      alert('Sprawdź konsolę: lista plików i ich publiczne URL-e');
    } catch (err) {
      alert('Błąd debugowania: ' + err.message);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Nie zamykaj formularza gdy podgląd jest otwarty
          if (previewOpen || tenantPreviewOpen) return;
          onClose();
        }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: '90vh',
          m: isMobile ? 0 : 2,
          backgroundColor: '#FAFAFA'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header - clean style like in attached image */}
        <Box sx={{ 
          p: { xs: 3, sm: 4 }, 
          pb: 2,
          backgroundColor: '#FAFAFA',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 3,
            position: 'relative'
          }}>
            <Box
              component="img"
              src={logoImage}
              alt="Logo"
              sx={{
                height: 53,
                width: 'auto'
              }}
            />
            <IconButton 
              onClick={onClose} 
              sx={{ 
                color: '#999', 
                p: 1,
                position: 'absolute',
                top: -16,
                right: -16
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="h4" sx={{ 
            fontWeight: '600',
            color: '#111',
            mb: 1,
            fontSize: { xs: '24px', sm: '28px' }
          }}>
            {activeTab === 1
              ? (tenantEditMode ? 'Edytuj swój profil' : 'Dodaj swój profil')
              : (editMode ? 'Edytuj ogłoszenie' : 'Dodaj ogłoszenie')}
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#666',
            fontSize: '16px',
            lineHeight: 1.5,
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            {isFirstTime && !editMode && !tenantEditMode
              ? 'Zajmie Ci to tylko 2 min i jest całkowicie za darmo'
              : activeTab === 0 
                ? 'Wypełnij formularz, żeby dodać swoje mieszkanie do naszego portalu.'
                : 'Dodaj swój profil a wynajmujący sami się do Ciebie odezwą z propozycją mieszkania, którego szukasz.'
            }
          </Typography>
        </Box>

        {/* Tabs Navigation */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: 'white'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: '600',
                fontSize: '16px',
                minHeight: 60,
                px: 4,
                '&:hover': {
                  color: '#374151'
                },
                '&:focus': {
                  color: '#374151'
                },
                '&.Mui-selected': {
                  color: '#D2691E'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#D2691E',
                height: 3
              }
            }}
          >
            <Tab 
              value={1}
              icon={<PersonIcon />}
              label="Dodaj profil najemcy" 
              sx={{ 
                color: activeTab === 1 ? '#D2691E' : '#666',
                '& .MuiSvgIcon-root': {
                  color: activeTab === 1 ? '#D2691E' : '#999',
                  mb: 0.5
                }
              }} 
            />
            <Tab 
              value={0}
              icon={<HomeIcon />}
              label="Dodaj mieszkanie" 
              sx={{ 
                color: activeTab === 0 ? '#D2691E' : '#666',
                '& .MuiSvgIcon-root': {
                  color: activeTab === 0 ? '#D2691E' : '#999',
                  mb: 0.5
                }
              }} 
            />
          </Tabs>
        </Box>

        {/* Form Content */}
        {activeTab === 0 ? (
          // Formularz mieszkania
          <Box component="form" onSubmit={handleSubmit} sx={{ 
            p: { xs: 3, sm: 4 },
            backgroundColor: 'white',
            maxWidth: { xs: '500px', md: '650px' },
            margin: '0 auto',
            '& .MuiInputBase-input': {
              fontSize: '14px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
            },
            '& .MuiSelect-select': {
              fontSize: '14px',
            }
          }}>
          
          {/* Podstawowe informacje */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Podstawowe informacje
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cena (zł/mies.)"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange('price')}
                  required={isFieldRequired}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Czynsz (zł)"
                  type="number"
                  value={formData.rent}
                  onChange={handleInputChange('rent')}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Powierzchnia (m²)"
                  type="number"
                  value={formData.area}
                  onChange={handleInputChange('area')}
                  required={isFieldRequired}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  select
                  label="Liczba pokoi"
                  value={formData.rooms}
                  onChange={handleInputChange('rooms')}
                  required={isFieldRequired}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <MenuItem key={num} value={num}>{num === 1 ? 'Kawalerka' : `${num} pokoje`}</MenuItem>
                  ))}
                  <MenuItem key={0} value={0}>Pokój w mieszkaniu</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Wysokość kaucji (zł)"
                  type="number"
                  value={formData.deposit}
                  onChange={handleInputChange('deposit')}
                  required={isFieldRequired}
                  placeholder="np. 3500"
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AvailableFromField
                  value={formData.availableFrom}
                  required={isFieldRequired}
                  onChange={handleInputChange('availableFrom')}
                />
             </Grid>
            </Grid>
          </Box>

          {/* Adres */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Adres
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={citySuggestions}
                  getOptionLabel={(option) => option?.displayName || option?.name || option || ''}
                  isOptionEqualToValue={(option, value) => (option?.name || option) === (value?.name || value)}
                  value={formData.city || ''}
                  inputValue={cityInputValue}
                  onInputChange={handleCityInputChange}
                  onChange={handleCityChange}
                  onOpen={() => {
                    if (citySuggestions.length === 0) {
                      const popularCities = getPopularCities();
                      setCitySuggestions(popularCities);
                    }
                  }}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const q = normalizePolishText(inputValue);
                    return options
                      .filter(opt => normalizePolishText(opt?.name || opt || '').includes(q))
                      .sort((a, b) => {
                        const la = normalizePolishText(a?.name || a || '');
                        const lb = normalizePolishText(b?.name || b || '');
                        if (la.startsWith(q) && !lb.startsWith(q)) return -1;
                        if (!la.startsWith(q) && lb.startsWith(q)) return 1;
                        return (a?.name || a || '').localeCompare(b?.name || b || '', 'pl');
                      });
                  }}
                  loading={isLoadingCities}
                  loadingText="Ładowanie miast..."
                  noOptionsText="Brak wyników"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      label="Miejscowość"
                      placeholder="Zacznij pisać nazwę miasta..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationIcon sx={{ color: '#EE2005' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {isLoadingCities ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        backgroundColor: '#FAFAFA',
                        borderRadius: 2,
                        cursor: 'text',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: '1px solid #E0E0E0' },
                          '&:hover fieldset': { borderColor: '#111' },
                          '&.Mui-focused fieldset': { borderColor: '#111', borderWidth: '1px' },
                          width: '100%',
                          minWidth: '200px'
                        }
                      }}
                    />
                  )}
                  sx={{ width: '100%' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                {formData.city ? (
                  <Autocomplete
                    freeSolo
                    options={districtSuggestions}
                    getOptionLabel={(option) => option?.displayName || option?.name || option || ''}
                    value={formData.district}
                    inputValue={districtInputValue}
                    onInputChange={handleDistrictInputChange}
                    onChange={handleDistrictChange}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const q = normalizePolishText(inputValue);
                      return options
                        .filter(opt => normalizePolishText(opt?.name || opt || '').includes(q))
                        .sort((a, b) => {
                          const la = normalizePolishText(a?.name || a || '');
                          const lb = normalizePolishText(b?.name || b || '');
                          if (la.startsWith(q) && !lb.startsWith(q)) return -1;
                          if (!la.startsWith(q) && lb.startsWith(q)) return 1;
                          return (a?.name || a || '').localeCompare(b?.name || b || '', 'pl');
                        });
                    }}
                    onOpen={() => {
                      if (formData.city && districtSuggestions.length === 0) {
                        fetchDistricts(formData.city, '').then(d => { if (d.length > 0) setDistrictSuggestions(d); }).catch(() => {
                          setDistrictSuggestions(getDistrictsForCity(formData.city));
                        });
                      }
                    }}
                    noOptionsText="Brak dzielnic dla tego miasta"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dzielnica"
                        placeholder="Wybierz dzielnicę..."
                        sx={{
                          '& .MuiInputBase-root': {
                            width: '100%',
                            minWidth: '200px'
                          }
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Dzielnica"
                    value={formData.district}
                    onChange={handleInputChange('district')}
                    placeholder={formData.city ? "Wpisz dzielnicę..." : "Najpierw wybierz miasto"}
                    disabled={!formData.city}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <TextField
                    inputRef={streetInputRef}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label={<span>Nazwa ulicy <span style={{color: '#FF8C00'}}>(opcjonalnie)</span></span>}
                    placeholder={formData.city ? 'np. Marszałkowska, Nowy Świat…' : 'Najpierw wybierz miasto'}
                    value={streetInputValue}
                    onChange={(e) => handleStreetInputChange(e.target.value)}
                    onFocus={() => {
                      if (formData.city && streetInputValue.length >= 2) {
                        setStreetDropdownOpen(true);
                      }
                    }}
                    onBlur={() => {
                      setStreetTouched(true);
                      // Delay close so onMouseDown on option fires first
                      setTimeout(() => setStreetDropdownOpen(false), 150);
                    }}
                    disabled={!formData.city}
                    required={isFieldRequired}
                    error={streetTouched && formData.street && formData.street.trim().length < 4}
                    helperText={streetTouched && formData.street && formData.street.trim().length < 4 ? 'Nazwa ulicy musi mieć min. 4 znaki' : ''}
                    InputProps={{
                      endAdornment: isLoadingStreets ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null
                    }}
                    sx={{
                      '& .MuiInputBase-root': { width: '100%', minWidth: '200px' }
                    }}
                  />
                  {streetDropdownOpen && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1400,
                        maxHeight: 220,
                        overflowY: 'auto',
                        boxShadow: 3,
                        mt: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {isLoadingStreets && streetSuggestions.length === 0 && (
                        <Box sx={{ px: 2, py: 1.5, color: 'text.secondary', fontSize: 14 }}>Szukam ulic…</Box>
                      )}
                      {!isLoadingStreets && streetSuggestions.length === 0 && (
                        <Box sx={{ px: 2, py: 1.5, color: 'text.secondary', fontSize: 14 }}>Brak wyników</Box>
                      )}
                      {streetSuggestions.map((street) => (
                        <MenuItem
                          key={street.id || street.name}
                          onMouseDown={(e) => {
                            e.preventDefault(); // prevent blur before click
                            handleSelectStreet(street);
                          }}
                          sx={{ fontSize: 14, py: 1 }}
                        >
                          {street.name}
                        </MenuItem>
                      ))}
                    </Paper>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Szczegóły budynku */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Szczegóły budynku
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Piętro"
                  type="number"
                  value={formData.floor}
                  onChange={handleInputChange('floor')}
                  required={isFieldRequired}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Liczba pięter"
                  type="number"
                  value={formData.totalFloors}
                  onChange={handleInputChange('totalFloors')}
                  required={isFieldRequired}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Udogodnienia */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Udogodnienia <span style={{color: '#FF8C00', fontWeight: 'normal', textTransform: 'none'}}>(opcjonalnie)</span>
            </Typography>
            <FormGroup>
              <Grid container spacing={1}>
                {availableAmenities.map((amenity) => (
                  <Grid item xs={6} sm={6} key={amenity}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.amenities.includes(amenity)}
                          onChange={() => handleCheckboxChange('amenities', amenity)}
                          size="small"
                          sx={{
                            color: '#999',
                            '&.Mui-checked': { color: '#111' },
                            p: 0.5,
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontSize: '13px' }}>{amenity}</Typography>}
                      sx={{ minHeight: 36, mr: 0, width: '100%', ml: 0, gap: '4px' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Box>

          {/* Wyposażenie */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Wyposażenie <span style={{color: '#FF8C00', fontWeight: 'normal', textTransform: 'none'}}>(opcjonalnie)</span>
            </Typography>
            <FormGroup>
              <Grid container spacing={1}>
                {availableEquipment.map((equipment) => (
                  <Grid item xs={6} sm={6} key={equipment}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.equipment.includes(equipment)}
                          onChange={() => handleCheckboxChange('equipment', equipment)}
                          size="small"
                          sx={{
                            color: '#999',
                            '&.Mui-checked': { color: '#111' },
                            p: 0.5,
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontSize: '13px' }}>{equipment}</Typography>}
                      sx={{ minHeight: 36, mr: 0, width: '100%', ml: 0, gap: '4px' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Box>

          {/* Upload zdjęć */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Zdjęcia (max 10)
            </Typography>
            
            <input
              id="photo-upload"
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png"
              multiple
              onChange={handlePhotoUpload}
            />
            <label htmlFor="photo-upload">
              <Box
                sx={{
                  border: '2px dashed #E0E0E0',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#FAFAFA',
                  '&:hover': {
                    borderColor: '#111',
                    backgroundColor: '#F5F5F5'
                  }
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                  Kliknij aby dodać zdjęcia
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  {formData.photos.length}/10 zdjęć
                </Typography>
              </Box>
            </label>

            {/* Podgląd zdjęć */}
            {formData.photos.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {formData.photos.map((photo, index) => (
                  <Grid item xs={4} sm={3} key={index}>
                    <Box
                      draggable
                      onDragStart={() => setDragIndex(index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handlePhotoDrop(index)}
                      onDragEnd={() => setDragIndex(null)}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'grab',
                        opacity: dragIndex === index ? 0.4 : 1,
                        transition: 'opacity 0.2s',
                        '&:active': { cursor: 'grabbing' }
                      }}
                    >
                      <img
                        src={photo instanceof File ? URL.createObjectURL(photo) : photo}
                        alt={photo instanceof File ? photo.name : 'Zdjęcie'}
                        style={{
                          width: '100%',
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: dragIndex === index ? '2px solid #D2691E' : '1px solid #E0E0E0',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                        }}
                      />
                      {index === 0 && (
                        <Typography variant="caption" sx={{
                          position: 'absolute', bottom: 4, left: 4,
                          backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                          px: 0.5, borderRadius: 1, fontSize: '10px'
                        }}>Główne</Typography>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: '#FF4444',
                          color: 'white',
                          '&:hover': { backgroundColor: '#CC0000' },
                          width: 24,
                          height: 24,
                          zIndex: 2
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Opis */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Typography variant="body2" sx={{ 
                color: '#374151', 
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Opis
              </Typography>
              {titleTouched && formData.title && formData.title.length > 0 && formData.title.length < 28 && (
                <Typography variant="caption" sx={{ color: '#DC2626', fontSize: '11px' }}>
                  Tytuł za krótki — min. 28 znaków
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Tytuł ogłoszenia"
                value={formData.title}
                onChange={handleInputChange('title')}
                onBlur={() => setTitleTouched(true)}
                required={isFieldRequired}
                placeholder="np. Piękne 3-pokojowe mieszkanie w centrum (min. 28 znaków)"
                sx={{
                  '& .MuiInputBase-root': {
                    width: '100%',
                    minWidth: '200px'
                  }
                }}
              />
              
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Szczegółowy opis"
                multiline
                rows={7}
                value={formData.description}
                onChange={handleInputChange('description')}
                onBlur={() => setDescriptionTouched(true)}
                required={isFieldRequired}
                placeholder="Opisz szczegółowo mieszkanie, jego zalety, lokalizację, najbliższą infrastrukturę, dojazd komunikacją publiczną, zalety dzielnicy... (min. 100 znaków)"
                helperText={descriptionTouched && formData.description && formData.description.length > 0 && formData.description.length < 100 ? 'Opis za krótki — min. 100 znaków' : ''}
                FormHelperTextProps={{ sx: { color: '#DC2626' } }}
                sx={{
                  '& .MuiInputBase-root': {
                    width: '100%',
                    minWidth: '200px'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Dane kontaktowe */}
          <Box sx={{ 
            mb: 4,
            p: 3,
            backgroundColor: '#F9FAFB',
            borderRadius: 2,
            border: '1px solid #E5E7EB'
          }}>
            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#374151', 
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Dane kontaktowe
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Imię"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  required={isFieldRequired}
                  error={formData.firstName && formData.firstName.trim().length > 0 && formData.firstName.trim().length < 3}
                  helperText={formData.firstName && formData.firstName.trim().length > 0 && formData.firstName.trim().length < 3 ? 'Min. 3 znaki' : ''}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Numer telefonu (Polska +48)"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  required={isFieldRequired}
                  placeholder="*********"
                  inputProps={{ maxLength: 9 }}
                  error={formData.phone && formData.phone.replace(/\D/g, '').length !== 9}
                  helperText={formData.phone && formData.phone.replace(/\D/g, '').length !== 9 ? 'Numer musi mieć dokładnie 9 cyfr' : ''}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Adres e-mail"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  onBlur={() => setEmailTouched(true)}
                  required={isFieldRequired}
                  placeholder="twoj@email.com"
                  error={emailTouched && formData.email && (!formData.email.includes('@') || formData.email.indexOf('@') < 1 || formData.email.length - formData.email.indexOf('@') - 1 < 4)}
                  helperText={emailTouched && formData.email && (!formData.email.includes('@') || formData.email.indexOf('@') < 1 || formData.email.length - formData.email.indexOf('@') - 1 < 4) ? 'Podaj poprawny adres email' : ''}
                  sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Opcja premium - wyróżnij ogłoszenie */}
          {/* TYMCZASOWO WYŁĄCZONE - włączyć gdy serwis nabierze ruchu */}
          {/* {!editMode && activeTab === 0 && (
            <PremiumListingOption
              isPurchased={false}
              onBuyClick={null}
            />
          )} */}

          {/* Zgoda na politykę prywatności */}
          {!editMode && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyPolicyAccepted}
                    onChange={(e) => {
                      setPrivacyPolicyAccepted(e.target.checked);
                      if (validationError) setValidationError('');
                    }}
                    sx={{ color: '#999', '&.Mui-checked': { color: '#111' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                    Akceptuję{' '}
                    <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#111', fontSize: 'inherit' }}>
                      politykę prywatności
                    </Typography>
                    {' '}oraz{' '}
                    <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#111', fontSize: 'inherit' }}>
                      regulamin serwisu
                    </Typography>
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Validation Error Message */}
          {validationError && (
            <Typography
              sx={{
                color: 'error.main',
                fontSize: '14px',
                mb: 2,
                textAlign: 'left',
                fontWeight: '500',
                whiteSpace: 'pre-line',
                backgroundColor: '#fff5f5',
                border: '1px solid #f44336',
                borderRadius: 2,
                p: 2
              }}
            >
              {validationError}
            </Typography>
          )}

          {/* Button podglądu */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handlePreview}
            sx={{
              borderColor: '#111',
              color: '#111',
              py: 2,
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: 2,
              textTransform: 'none',
              mb: 2,
              '&:hover': {
                borderColor: '#333',
                color: '#333',
                backgroundColor: 'rgba(17, 17, 17, 0.04)'
              }
            }}
          >
            Podgląd Twojego ogłoszenia
          </Button>

          {/* Button publikacji */}
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{
                backgroundColor: publishSuccess ? '#28A745' : '#111',
                color: 'white',
                py: 2,
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: 2,
                textTransform: 'none',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: publishSuccess ? '#28A745' : '#333'
                },
                '&.Mui-disabled': publishSuccess ? {
                  backgroundColor: '#28A745',
                  color: 'white'
                } : {}
              }}
            >
              {buttonText}
            </Button>
            {isSubmitting && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                <Box sx={{ width: '100%', height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
                  <Box sx={{ width: `${progress}%`, height: 4, backgroundColor: '#16a34a', borderRadius: 2, transition: 'width 0.1s' }} />
                </Box>
              </Box>
            )}
          </Box>
          </Box>
        ) : (
          // ===== FORMULARZ NAJEMCY =====
      <Box component="form" onSubmit={handleTenantSubmit} sx={{ 
            p: { xs: 3, sm: 4 },
            backgroundColor: 'white',
            maxWidth: { xs: '500px', md: '650px' },
            margin: '0 auto',
            '& .MuiInputBase-input': {
              fontSize: '14px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
            },
            '& .MuiSelect-select': {
              fontSize: '14px',
            }
          }}>
            {/* Zdjęcie profilowe */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB',
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                Zdjęcie profilowe
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 2
              }}>
                {tenantData.profilePhoto ? (
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={tenantData.profilePhoto instanceof File 
                        ? URL.createObjectURL(tenantData.profilePhoto) 
                        : tenantData.profilePhoto}
                      sx={{ 
                        width: 120, 
                        height: 120,
                        border: '3px solid #D2691E'
                      }}
                    />
                    <IconButton
                      onClick={() => setTenantData(prev => ({ ...prev, profilePhoto: null }))}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        backgroundColor: 'white',
                        color: '#f44336',
                        '&:hover': { backgroundColor: '#ffebee' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      borderStyle: 'dashed',
                      borderColor: '#D2691E',
                      color: '#D2691E',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 40 }} />
                    <Typography variant="caption">Dodaj zdjęcie</Typography>
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleProfilePhotoUpload}
                    />
                  </Button>
                )}
              </Box>
            </Box>

            {/* Dane osobowe */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                Dane osobowe
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Imię"
                    value={tenantData.firstName}
                    onChange={handleTenantInputChange('firstName')}
                    required={isFieldRequired}
                    error={tenantData.firstName && tenantData.firstName.trim().length > 0 && tenantData.firstName.trim().length < 3}
                    helperText={tenantData.firstName && tenantData.firstName.trim().length > 0 && tenantData.firstName.trim().length < 3 ? 'Min. 3 znaki' : ''}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Wiek"
                    select
                    value={tenantData.age}
                    onChange={handleTenantInputChange('age')}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  >
                    <MenuItem value="">Nie podaję</MenuItem>
                    <MenuItem value="18">18+</MenuItem>
                    <MenuItem value="20">20+</MenuItem>
                    <MenuItem value="30">30+</MenuItem>
                    <MenuItem value="40">40+</MenuItem>
                    <MenuItem value="50">50+</MenuItem>
                    <MenuItem value="60">60+</MenuItem>
                    <MenuItem value="70">70+</MenuItem>
                    <MenuItem value="80">80+</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<span>Zawód <span style={{color: '#FF8C00'}}>(opcjonalnie)</span></span>}
                    value={tenantData.profession}
                    onChange={handleTenantInputChange('profession')}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={tenantData.email}
                    onChange={handleTenantInputChange('email')}
                    onBlur={() => setTenantEmailTouched(true)}
                    required={isFieldRequired}
                    error={tenantEmailTouched && tenantData.email && (!tenantData.email.includes('@') || tenantData.email.indexOf('@') < 1 || tenantData.email.length - tenantData.email.indexOf('@') - 1 < 4)}
                    helperText={tenantEmailTouched && tenantData.email && (!tenantData.email.includes('@') || tenantData.email.indexOf('@') < 1 || tenantData.email.length - tenantData.email.indexOf('@') - 1 < 4) ? 'Podaj poprawny adres email' : ''}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Ile chcesz przeznaczyć mies. na wynajem */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                Jaki jest Twój budżet mies.?
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Budżet mies. min (zł)"
                    type="number"
                    value={tenantData.priceRange.min}
                    onChange={handleTenantPriceRangeChange('min')}
                    InputProps={{ inputProps: { min: 0 } }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Budżet mies. max (zł)"
                    type="number"
                    value={tenantData.priceRange.max}
                    onChange={handleTenantPriceRangeChange('max')}
                    InputProps={{ inputProps: { min: 0 } }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Preferencje mieszkania */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                Preferencje mieszkania
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={tenantCitySuggestions}
                    getOptionLabel={(option) => option?.displayName || option?.name || option || ''}
                    isOptionEqualToValue={(option, value) => (option?.name || option) === (value?.name || value)}
                    value={tenantData.preferredCity || ''}
                    inputValue={tenantCityInputValue}
                    onInputChange={handleTenantCityInputChange}
                    onChange={handleTenantCityChange}
                    onOpen={() => {
                      if (tenantCitySuggestions.length === 0) {
                        const popularCities = getPopularCities();
                        setTenantCitySuggestions(popularCities);
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const q = normalizePolishText(inputValue);
                      return options
                        .filter(opt => normalizePolishText(opt?.name || opt || '').includes(q))
                        .sort((a, b) => {
                          const la = normalizePolishText(a?.name || a || '');
                          const lb = normalizePolishText(b?.name || b || '');
                          if (la.startsWith(q) && !lb.startsWith(q)) return -1;
                          if (!la.startsWith(q) && lb.startsWith(q)) return 1;
                          return (a?.name || a || '').localeCompare(b?.name || b || '', 'pl');
                        });
                    }}
                    loading={isLoadingTenantCities}
                    loadingText="Ładowanie miast..."
                    noOptionsText="Brak wyników"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        label="Preferowane miasto"
                        placeholder="Zacznij pisać nazwę miasta..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon sx={{ color: '#EE2005' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {isLoadingTenantCities ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            width: '100%',
                            minWidth: '200px'
                          }
                        }}
                      />
                    )}
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {tenantData.preferredCity ? (
                    <Autocomplete
                      freeSolo
                      options={tenantDistrictSuggestions}
                      getOptionLabel={(option) => option?.displayName || option?.name || option || ''}
                      value={tenantData.preferredDistrict}
                      inputValue={tenantDistrictInputValue}
                      onInputChange={handleTenantDistrictInputChange}
                      onChange={handleTenantDistrictChange}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const q = normalizePolishText(inputValue);
                        return options
                          .filter(opt => normalizePolishText(opt?.name || opt || '').includes(q))
                          .sort((a, b) => {
                            const la = normalizePolishText(a?.name || a || '');
                            const lb = normalizePolishText(b?.name || b || '');
                            if (la.startsWith(q) && !lb.startsWith(q)) return -1;
                            if (!la.startsWith(q) && lb.startsWith(q)) return 1;
                            return (a?.name || a || '').localeCompare(b?.name || b || '', 'pl');
                          });
                      }}
                      onOpen={() => {
                        if (tenantData.preferredCity && tenantDistrictSuggestions.length === 0) {
                          fetchDistricts(tenantData.preferredCity, '').then(d => { if (d.length > 0) setTenantDistrictSuggestions(d); }).catch(() => {
                            setTenantDistrictSuggestions(getDistrictsForCity(tenantData.preferredCity));
                          });
                        }
                      }}
                      noOptionsText="Brak dzielnic dla tego miasta"
                      renderInput={(params) => (
                          <TextField
                          {...params}
                          label="Preferowana dzielnica"
                          placeholder="Wybierz dzielnicę..."
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiInputBase-root': {
                              width: '100%',
                              minWidth: '200px'
                            }
                          }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Preferowana dzielnica"
                      value={tenantData.preferredDistrict}
                      onChange={handleTenantInputChange('preferredDistrict')}
                      placeholder={tenantData.preferredCity ? "Wpisz dzielnicę..." : "Najpierw wybierz miasto"}
                      disabled={!tenantData.preferredCity}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-root': {
                          width: '100%',
                          minWidth: '200px'
                        }
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    }
                  }}>
                    <InputLabel shrink>Liczba pokoi</InputLabel>
                    <Select
                      value={tenantData.preferredRooms}
                      onChange={handleTenantInputChange('preferredRooms')}
                      label="Liczba pokoi"
                      displayEmpty
                    >
                      <MenuItem value="">Dowolna</MenuItem>
                      <MenuItem value="1">Kawalerka</MenuItem>
                      <MenuItem value="2">2 pokoje</MenuItem>
                      <MenuItem value="3">3 pokoje</MenuItem>
                      <MenuItem value="4">4+ pokoje</MenuItem>
                      <MenuItem value="0">Pokój w mieszkaniu</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
                    const input = document.getElementById('moveInDate-date-input');
                    if (!input) return;
                    if (input.showPicker) {
                      try { input.showPicker(); } catch(e) { input.focus(); }
                    } else {
                      input.focus();
                      input.click();
                    }
                  }}>
                    <TextField
                      fullWidth
                      label="Od kiedy?"
                      value={tenantData.moveInDate ? new Date(tenantData.moveInDate).toLocaleDateString('pl-PL') : ''}
                      placeholder="Wybierz datę"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarIcon sx={{ color: '#666' }} />
                          </InputAdornment>
                        ),
                        sx: {
                          cursor: 'pointer',
                          pointerEvents: 'none'
                        }
                      }}
                      sx={{
                        pointerEvents: 'none',
                        cursor: 'pointer',
                        '& .MuiInputBase-root': {
                          width: '100%',
                          minWidth: '200px'
                        }
                      }}
                    />
                    <input
                      id="moveInDate-date-input"
                      type="date"
                      value={tenantData.moveInDate}
                      onChange={handleTenantInputChange('moveInDate')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        zIndex: -1
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<span>Min m² <span style={{color: '#FF8C00'}}>(opcjonalnie)</span></span>}
                    type="text"
                    inputMode="numeric"
                    value={tenantData.preferredAreaMin}
                    onChange={handleTenantInputChange('preferredAreaMin')}
                    InputProps={{ inputProps: { pattern: '[0-9]*', inputMode: 'numeric' } }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<span>Max m² <span style={{color: '#FF8C00'}}>(opcjonalnie)</span></span>}
                    type="text"
                    inputMode="numeric"
                    value={tenantData.preferredAreaMax}
                    onChange={handleTenantInputChange('preferredAreaMax')}
                    InputProps={{ inputProps: { pattern: '[0-9]*', inputMode: 'numeric' } }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Udogodnienia */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                Udogodnienia <span style={{color: '#FF8C00', fontWeight: 'normal', textTransform: 'none'}}>(opcjonalnie)</span>
              </Typography>

              <Grid container spacing={1}>
                {['Balkon', 'Garaż', 'Winda', 'Piwnica', 'Internet', 'Ogródek', 'Ostatnie piętro', 'Parter', 'Klimatyzacja', 'Piekarnik', 'Zmywarka'].map((amenity) => (
                  <Grid item xs={6} sm={6} key={amenity}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tenantData.preferredAmenities?.includes(amenity) || false}
                          onChange={handleTenantAmenityChange(amenity)}
                          size="small"
                          sx={{
                            color: '#999',
                            '&.Mui-checked': { color: '#111' },
                            p: 0.5,
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontSize: '13px' }}>{amenity}</Typography>}
                      sx={{ minHeight: 36, mr: 0, width: '100%', ml: 0, gap: '4px' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Opis */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#F9FAFB',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }}>
              <Typography variant="body2" sx={{ 
                mb: 3, 
                fontWeight: '600', 
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '12px'
              }}>
                O mnie
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Napisz kilka słów o sobie np. czym się zajmujesz"
                value={tenantData.description}
                onChange={handleTenantInputChange('description')}
                onBlur={() => setTenantDescTouched(true)}
                error={tenantDescTouched && tenantData.description && tenantData.description.length > 0 && tenantData.description.length < 30}
                helperText={
                  tenantDescTouched && tenantData.description && tenantData.description.length > 0 && tenantData.description.length < 30
                    ? 'Min. 30 znaków'
                    : `${(tenantData.description || '').length}/650 znaków`
                }
              />
              
              {/* Checkbox niepalący */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!tenantData.smoker}
                      onChange={(e) => {
                        setTenantData(prev => ({
                          ...prev,
                          smoker: !e.target.checked
                        }));
                      }}
                      size="small"
                      sx={{
                        color: '#28A745',
                        '&.Mui-checked': { color: '#28A745' },
                        p: 0.5,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>Niepalący</Typography>}
                  sx={{ minHeight: 36, mr: 0, ml: 0, gap: '4px' }}
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tenantData.petOwner || false}
                      onChange={(e) => {
                        setTenantData(prev => ({
                          ...prev,
                          petOwner: e.target.checked
                        }));
                      }}
                      size="small"
                      sx={{
                        color: '#28A745',
                        '&.Mui-checked': { color: '#28A745' },
                        p: 0.5,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>Posiadam psa lub kota</Typography>}
                  sx={{ minHeight: 36, mr: 0, ml: 0, gap: '4px' }}
                />
              </Box>
            </Box>

            {/* Wyświetlanie błędów walidacji */}
            {validationError && (
              <Typography
                sx={{
                  color: 'error.main',
                  fontSize: '14px',
                  mb: 2,
                  textAlign: 'left',
                  fontWeight: '500',
                  whiteSpace: 'pre-line',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #f44336',
                  borderRadius: 2,
                  p: 2
                }}
              >
                {validationError}
              </Typography>
            )}

            {/* Przyciski */}
            {!editMode && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tenantPrivacyAccepted}
                      onChange={(e) => {
                        setTenantPrivacyAccepted(e.target.checked);
                        if (validationError) setValidationError('');
                      }}
                      sx={{ color: '#D2691E', '&.Mui-checked': { color: '#D2691E' } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                      Akceptuję{' '}
                      <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#D2691E', fontSize: 'inherit' }}>
                        politykę prywatności
                      </Typography>
                      {' '}oraz{' '}
                      <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#D2691E', fontSize: 'inherit' }}>
                        regulamin serwisu
                      </Typography>
                    </Typography>
                  }
                />
              </Box>
            )}

            {/* Przyciski akcji */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleTenantPreview}
              sx={{
                py: 2,
                mb: 2,
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: 2,
                textTransform: 'none',
                borderColor: '#D2691E',
                color: '#D2691E',
                '&:hover': {
                  backgroundColor: 'rgba(210, 105, 30, 0.08)',
                  borderColor: '#D2691E'
                }
              }}
            >
              Podgląd profilu najemcy
            </Button>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{
                backgroundColor: publishSuccess ? '#28A745' : '#111',
                color: 'white',
                py: 2,
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: 2,
                textTransform: 'none',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: publishSuccess ? '#28A745' : '#333'
                },
                '&.Mui-disabled': publishSuccess ? {
                  backgroundColor: '#28A745',
                  color: 'white'
                } : {}
              }}
            >
              {buttonText}
            </Button>
            {isSubmitting && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                <Box sx={{ width: '100%', height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
                  <Box sx={{ width: `${progress}%`, height: 4, backgroundColor: '#16a34a', borderRadius: 2, transition: 'width 0.1s' }} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      </Dialog>
      
      {/* Property Preview Modal */}
      {previewProperty && (
        <PropertyDetailsModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          property={previewProperty}
          onPublish={handlePropertyPublishFromModal}
          isSubmitting={isSubmitting}
          progress={progress}
          isEditMode={previewIsEditMode}
          publishSuccess={publishSuccess}
          buttonText={buttonText}
        />
      )}
      
      {/* Tenant Preview Modal */}
      {previewTenant && (
        <TenantDetailsModal
          open={tenantPreviewOpen}
          onClose={() => setTenantPreviewOpen(false)}
          tenant={previewTenant}
          onPublish={handleTenantPublishFromModal}
          isSubmitting={isSubmitting}
          progress={progress}
          publishSuccess={publishSuccess}
          isEditMode={tenantEditMode}
          buttonText={buttonText}
        />
      )}
      
      {/* <PaymentModal
        open={paymentModalOpen}
        onClose={handlePaymentClose}
        onPaymentSuccess={handlePaymentSuccess}
      /> */}
    </>
  );
};

export default AddListingFormNew;